import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import * as Sentry from '@sentry/react';
import axios, { AxiosResponse } from 'axios';
import ReactDOM from 'react-dom/client';
import { Provider, useDispatch, useSelector } from 'react-redux';
import App from './App';
import { injectStore } from './auth/http/authHttpInterceptor';
import store from './core/store/store';
import './index.css';
import reportWebVitals from './reportWebVitals';
import './sentry-init';

// import i18n for the translations
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { CONFIG_FILE_URL } from './core/config-loader/consts/config-file-url';
import { configLoaderActions, selectAppConfig } from './core/config-loader/store/config-loader-slice';
import { AppConfig } from './core/config/appConfig';
import initFirebase from './core/config/firebaseConfig';
import setUpPreventCacheInterceptor from './core/http/interceptors/prevent-cache-http-interceptor';
import './i18n';

const AppRootLoader: React.FC<{ loadingText: string }> = ({ loadingText }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: 'primary.main',
          fontFamily: 'monospace',
          marginBottom: 2,
        }}
      >
        {loadingText}
      </Typography>
      <CircularProgress />
    </Box>
  );
};

injectStore(store);
setUpPreventCacheInterceptor();

const InitializeApplication: React.FC = () => {
  const dispatch = useDispatch();
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);

  const appConfig = useSelector(selectAppConfig);

  useEffect(() => {
    (async () => {
      try {
        const response: AxiosResponse<AppConfig> = await axios.get(CONFIG_FILE_URL);
        console.log(`Vendor: ${response.data.vendor}`);
        console.log(`Environment: ${response.data.environment}`);
        console.log(`Version: ${response.data.appVersion}`);
        dispatch(configLoaderActions.fetchConfig());

        initFirebase(response.data.firebase);

        setFirebaseInitialized(true);
      } catch (err) {
        Sentry.captureException(err);
      }
    })();
  }, []);

  if (!firebaseInitialized || !Object.keys(appConfig).length) {
    return <AppRootLoader loadingText="Initializing Firebase & App Configuration..." />;
  }

  return <App />;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <Provider store={store}>
    <InitializeApplication />
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
